import React from 'react';
import { camelizeKeys } from 'humps';
import { AxiosError } from 'axios';
import { Helmet } from '../../components/Helmet';
import { DailyWagArticlePageContainer } from '../../containers/DailyWagArticlePageContainer';
import { PreloadImage } from '../../components/PreloadImage';
import { ArticleResponse, BodyObject, ShareUrl, CamelizedArticleResponse } from '../../types/api';
import { requestHandler } from '../../utils/requestHandler';
import { tryCatch } from '../../utils/tryCatch';
import { isNotFoundException } from '../../utils/api/isNotFoundException';
import { NotFoundException } from '../../utils/exceptions/NotFoundException';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import { getDailyWagBlogArticleComments } from '../../hooks/api/useGetDailyWagBlogArticleComments';
import { DEFAULT_CACHE_POLICY, HttpHeaderKey, WagContentApiEndpoint, WAG_CONTENT_API_URL } from '../../constants';
import { postGiftCardStatus } from '../../hooks';
import { httpGet } from '../../wag-react/common/api/api';
import { HttpClientResponse } from '../../wag-react/common/api';
import { DailyWagLayout } from '../../components/DailyWagLayout';
type Props = Pick<CamelizedArticleResponse, 'author' | 'readTime' | 'commentList' | 'relatedArticles'> & {
  meta: {
    metaTitle?: string;
    metaDesc?: string;
  };
  body: Array<BodyObject>;
  structuredData: {
    article: unknown;
    videos: unknown[];
  };
  title: string;
  heroImageUrl: string;
  slug: string;
  shareUrls: Array<ShareUrl>;
};
const DailyWagArticle = ({
  meta: {
    metaTitle,
    metaDesc
  },
  author,
  body,
  title,
  structuredData,
  slug,
  heroImageUrl,
  shareUrls,
  readTime,
  relatedArticles,
  commentList
}: Props) => {
  const {
    canonicalUrl
  } = useCanonicalUrl();
  return <>
      <Helmet canonical={canonicalUrl} title={metaTitle} description={metaDesc} robots="index, follow" data-sentry-element="Helmet" data-sentry-source-file="[daily-wag-article-slug].tsx" />
      <DailyWagLayout data-sentry-element="DailyWagLayout" data-sentry-source-file="[daily-wag-article-slug].tsx">
        <PreloadImage src={heroImageUrl} data-sentry-element="PreloadImage" data-sentry-source-file="[daily-wag-article-slug].tsx" />
        <DailyWagArticlePageContainer author={author} shareUrls={shareUrls} title={title} body={body} slug={slug} structuredData={structuredData} heroImageUrl={heroImageUrl} readTime={readTime} relatedArticles={relatedArticles} commentList={commentList} data-sentry-element="DailyWagArticlePageContainer" data-sentry-source-file="[daily-wag-article-slug].tsx" />
      </DailyWagLayout>
    </>;
};
const handleGetServerSideProps = requestHandler({
  headers: {
    [HttpHeaderKey.CacheControl]: DEFAULT_CACHE_POLICY
  }
});
export const getServerSideProps = handleGetServerSideProps(async ({
  query
}) => {
  const {
    'daily-wag-article-slug': dailyWagArticleSlug,
    stripe_session_id: stripeSessionId
  } = query;
  const slug = String(dailyWagArticleSlug);
  const dailyWagApiEndpoint = WagContentApiEndpoint.DailyWag.replace('{{slug}}', slug);
  const [response, error] = await tryCatch<
  // TODO type with expected daily wag endpoint response
  HttpClientResponse<ArticleResponse>, AxiosError>(() => httpGet(dailyWagApiEndpoint, {
    baseURL: WAG_CONTENT_API_URL,
    // To support 301/302 http statuses, do not follow redirects from responses
    maxRedirects: 0
  }));

  /**
   * We don't need to throw an error or return a 404 if there's an error
   * retrieving the comments; This request should not prevent the page from viewing.
   * If we want to make this a blocker request, remove the tryCatch and let it
   * throw an error (it will be caught by the `requestHandler`)
   */
  const [responseCommentList, errorCommentList] = await tryCatch(() => getDailyWagBlogArticleComments(slug));

  /**
   * Override the error when receiving 404,
   * otherwise, rethrow it and let `requestHandler` function handles it
   */
  if (error) {
    const statusCode = error.response.status;
    if (isNotFoundException(statusCode)) {
      throw NotFoundException(`Not found article ${slug}`);
    }
    throw error;
  }

  // TODO type transformedData properly when response is finalized
  const transformedData = camelizeKeys(response.data) as CamelizedArticleResponse;
  const {
    title,
    metaTitle,
    metaDesc,
    body,
    heroImageUrl,
    structuredData,
    shareUrls,
    ...rest
  } = transformedData;
  const commentList = errorCommentList ? {
    items: [],
    hasNextPage: false,
    totalCount: 0
  } : responseCommentList;

  /**
   * Before we render, let's check if there is a `stripe_session_id` on the URL
   * and if it does exist, let's call /api/v5/web/gift/status
   *
   * TODO - find a better/cleaner way
   * https://wagsters.atlassian.net/browse/SF-2364
   */

  if (stripeSessionId) {
    postGiftCardStatus({
      stripeSessionId: String(stripeSessionId),
      checkoutMethod: 'stripe'
    });
  }
  return {
    props: {
      title,
      body,
      slug,
      structuredData,
      heroImageUrl,
      shareUrls,
      meta: {
        metaTitle,
        metaDesc
      },
      commentList,
      ...rest
    }
  };
});
export default DailyWagArticle;